'use client';

import React from 'react';
import { FaCaretDown, FaCaretUp } from 'react-icons/fa';
import { Skeleton } from '../../../../components/ui/skeleton';

interface SummaryData {
  currencyName: string;
  startOfMonthRate: number | null;
  todayRate: number | null;
  todayChange: number | null;
  minMonthRate: number | null;
  maxMonthRate: number | null;
  latestUpdate: Date | null;
}

interface CurrencySummaryCardProps {
  summary: SummaryData | null;
  loading: boolean;
}

/**
 * ✅ Индикатор роста/падения курса
 */
function TodayChangeIndicator({ change }: { change: number | null }) {
  if (change === null || change === 0) return null;

  const isPositive = change > 0;
  const colorClass = isPositive ? 'text-green-500' : 'text-red-500';
  const label = `Изменение: ${isPositive ? 'повышение' : 'снижение'} на ${Math.abs(change).toFixed(2)} рублей`;

  return (
    <span className={`text-sm flex gap-1 items-center ${colorClass}`} aria-label={label}>
      {isPositive ? <FaCaretUp aria-hidden="true" /> : <FaCaretDown aria-hidden="true" />}
      {Math.abs(change).toFixed(2)}
    </span>
  );
}

/**
 * ✅ Один элемент сводки
 */
function SummaryItem({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col" role="group" aria-label={label}>
      <span className="text-gray-400 text-sm">{label}</span>
      <span className="text-white text-lg font-semibold">{value}</span>
    </div>
  );
}

/**
 * ✅ Скелетон для загрузки
 */
function SummarySkeleton() {
  return (
    <div className="flex flex-col" aria-hidden="true">
      <Skeleton className="w-32 h-4 mb-2" />
      <Skeleton className="w-24 h-6" />
    </div>
  );
}

/**
 * ✅ Основной компонент карточки
 */
export function CurrencySummaryCard({ summary, loading }: CurrencySummaryCardProps) {
  return (
    <section
      role="region"
      aria-labelledby="currency-summary-heading"
      className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 p-4 sm:p-6 bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-lg shadow-lg dark:bg-gradient-to-br dark:from-slate-800/50 dark:via-slate-700/60 dark:to-slate-800/50 dark:border dark:border-slate-700"
    >
      <h2 id="currency-summary-heading" className="sr-only">
        Сводка по валюте
      </h2>

      {loading || !summary ? (
        Array.from({ length: 5 }).map((_, i) => <SummarySkeleton key={i} />)
      ) : (
        <>
          <SummaryItem
            label="Валюта"
            value={<span aria-live="polite">{summary.currencyName}</span>}
          />
          <SummaryItem
            label="Курс на начало месяца"
            value={
              summary.startOfMonthRate !== null ? `${summary.startOfMonthRate.toFixed(2)} ₽` : 'N/A'
            }
          />

          <div className="flex flex-col" role="group" aria-label="Курс на сегодня">
            <span className="text-gray-400 text-sm">Курс на сегодня</span>
            <span className="text-white text-lg font-semibold flex items-center gap-2">
              {summary.todayRate !== null ? `${summary.todayRate.toFixed(2)} ₽` : 'N/A'}
              <TodayChangeIndicator change={summary.todayChange} />
            </span>
          </div>

          <SummaryItem
            label="Минимальный курс за месяц"
            value={summary.minMonthRate !== null ? `${summary.minMonthRate.toFixed(2)} ₽` : 'N/A'}
          />
          <SummaryItem
            label="Максимальный курс за месяц"
            value={summary.maxMonthRate !== null ? `${summary.maxMonthRate.toFixed(2)} ₽` : 'N/A'}
          />
        </>
      )}

      {!loading && summary?.latestUpdate && (
        <div
          className="sm:col-span-2 lg:col-span-5 text-right text-xs text-gray-400 pt-2"
          aria-label={`Последнее обновление: ${summary.latestUpdate.toLocaleString('ru-RU')}`}
        >
          Обновлено: {summary.latestUpdate.toLocaleString('ru-RU')}
        </div>
      )}
    </section>
  );
}
